import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Slide,
  TextField,
  Divider,
  List,
  ListItem
} from '@mui/material';
import {
  Close as CloseIcon,
  ArrowBackIos as PrevIcon,
  ArrowForwardIos as NextIcon,
  ChatBubbleOutline as CommentIcon,
  Send as SendIcon,
  Feedback as FeedbackIcon
} from '@mui/icons-material';
import styled from '@emotion/styled';
import axios from 'axios';
import { API } from "../../config/env";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const VideoContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  aspect-ratio: 16/9;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const VideoFrame = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
  aspect-ratio: 16/9;
  max-height: 70vh;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.5);
  background-color: #000;
`;

const VideoOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 30%, rgba(0,0,0,0) 70%, rgba(0,0,0,0.7) 100%);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 16px;
  box-sizing: border-box;
  opacity: 1;
  transition: opacity 0.3s ease;
  pointer-events: none;
  z-index: 5;

  &:hover {
    background: linear-gradient(to bottom, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.2) 30%, rgba(0,0,0,0.2) 70%, rgba(0,0,0,0.8) 100%);
  }
`;

const OverlayHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const OverlayFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
`;

const NavigationButton = styled(IconButton)`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background-color: rgba(114, 106, 106, 0.7);
  color: white;
  z-index: 10;
  &:hover {
    background-color: rgba(114, 106, 106, 1);
  }
`;

const CommentButton = styled(IconButton)`
  position: absolute;
  right: 48px;
  top: 8px;
  background-color: none;
  color: white;
  z-index: 10;
  margin-right: 8px;

  &:hover {
    background-color: #3a6ad4;
  }
`;

const CommentPanel = styled(Box)`
  width: 320px;
  background-color: #1a1a1a;
  color: white;
  padding: 16px;
  position: relative;
  height: 100%;
  overflow-y: auto;
  border-left: 1px solid #333;
  box-shadow: -2px 0 10px rgba(0, 0, 0, 0.3);
`;

const CommentList = styled(List)`
  padding: 0;
  margin-bottom: 16px;
  max-height: calc(100vh - 200px);
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CommentItem = styled(ListItem)`
  padding: 12px 0;
  display: block;
  background-color: #222;
  border-radius: 8px;
  margin-bottom: 8px;
  padding: 12px;
`;

const CommentText = styled(Typography)`
  color: #f1f1f1;
  font-size: 14px;
  margin-bottom: 8px;
  line-height: 1.4;
`;

const CommentDate = styled(Typography)`
  color: #999;
  font-size: 12px;
  font-weight: 400;
`;

const CommentForm = styled('form')`
  display: flex;
  align-items: center;
  margin-top: 16px;
`;

const CommentInput = styled(TextField)`
  flex: 1;
  margin-right: 8px;

  & .MuiOutlinedInput-root {
    background-color: #333;
    color: white;
    border-radius: 20px;
    font-size: 14px;

    & fieldset {
      border-color: #444;
    }

    &:hover fieldset {
      border-color: #666;
    }

    &.Mui-focused fieldset {
      border-color: #4880FF;
    }
  }

  & .MuiInputLabel-root {
    color: #999;
  }

  & .MuiInputBase-input::placeholder {
    color: #999;
    opacity: 1;
  }
`;

const CommentSubmitButton = styled(IconButton)`
  background-color: #4880FF;
  color: white;

  &:hover {
    background-color: #3a6ad4;
  }
`;

// Helper function to format time ago (e.g., "2 days ago", "1 day ago")
const formatTimeAgo = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInMs = now - date;
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

  if (diffInDays === 0) {
    // Less than a day
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    if (diffInHours === 0) {
      // Less than an hour
      const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
      return diffInMinutes <= 1 ? 'just now' : `${diffInMinutes} minutes`;
    }
    return diffInHours === 1 ? '1 hour' : `${diffInHours} hours`;
  } else if (diffInDays === 1) {
    return '1 day';
  } else {
    return `${diffInDays} days`;
  }
};

// Helper function to extract video ID from URL and create proper embed URL
const getVideoEmbedUrl = (url) => {
  try {

    // Handle dashboard URLs
    if (url.includes('dashboard.collegeit')) {
      try {
        // For dashboard URLs, we'll try to extract the video ID if possible
        const urlObj = new URL(url);
        const videoId = urlObj.searchParams.get('id') || urlObj.searchParams.get('v');

        if (videoId) {
          // console.log("Found dashboard video ID:", videoId);
          // If it contains a YouTube video ID, convert to YouTube embed
          if (url.includes('youtube') || url.includes('youtu.be')) {
            return `https://www.youtube.com/embed/${videoId}?autoplay=0&enablejsapi=1&origin=${window.location.origin}`;
          }
          // If it's a Google Drive video
          if (url.includes('drive.google')) {
            return `https://drive.google.com/file/d/${videoId}/preview`;
          }
        }

        // If we can't extract a specific format, return the URL as is
        return url;
      } catch (e) {
        console.error("Error parsing dashboard URL:", e);
      }
    }

    // Handle portal.collegeit.org URLs
    if (url.includes('portal.collegeit.org/watch') || url.includes('portal.collegeit')) {
      try {
        const urlObj = new URL(url);
        const videoId = urlObj.searchParams.get('id') || urlObj.searchParams.get('v');
        if (videoId) {
          // console.log("Found portal video ID:", videoId);
          // Return the same URL as it should be embeddable
          return url;
        }
      } catch (e) {
        console.error("Error parsing portal URL:", e);
      }
    }

    // Handle YouTube URLs
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      let videoId = '';

      // Extract video ID from various YouTube URL formats
      if (url.includes('youtube.com/watch')) {
        const urlParams = new URL(url).searchParams;
        videoId = urlParams.get('v');
      } else if (url.includes('youtu.be/')) {
        videoId = url.split('youtu.be/')[1].split('?')[0];
      } else if (url.includes('youtube.com/embed/')) {
        videoId = url.split('youtube.com/embed/')[1].split('?')[0];
      }

      if (videoId) {
        // Return proper YouTube embed URL with parameters for better embedding
        return `https://www.youtube.com/embed/${videoId}?autoplay=0&enablejsapi=1&origin=${window.location.origin}`;
      }
    }

    // Handle Google Drive URLs
    if (url.includes('drive.google.com/file/d/')) {
      // Extract file ID from Google Drive URL
      // Format: https://drive.google.com/file/d/{fileId}/view?usp=sharing
      const fileIdMatch = url.match(/\/file\/d\/([^\/]+)/);
      if (fileIdMatch && fileIdMatch[1]) {
        const fileId = fileIdMatch[1];
        // Return Google Drive embed URL
        return `https://drive.google.com/file/d/${fileId}/preview`;
      }
    }

    console.log("No matching URL pattern found, using default");
    // If it's not a recognized URL format, return the original URL
    return url;
  } catch (error) {
    console.error('Error parsing video URL:', error);
    // Return the original URL as fallback
    return url;
  }
};

const VideoPreviewCarousel = ({ open, onClose, videos, initialVideoIndex }) => {
  const [currentIndex, setCurrentIndex] = useState(initialVideoIndex || 0);
  const [commentsOpen, setCommentsOpen] = useState(true);
  const [comment, setComment] = useState('');
  const [allComments, setAllComments] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const currentVideo = videos[currentIndex];

  const fetchComments = async (videoId) => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${API.CORE}/api/v1/video-analytics/video/data/${videoId}`);
      
      // Update this to handle the actual response structure from your API
      if (response.data) {
        setAllComments(prevComments => ({
          ...prevComments,
          [videoId]: response.data.comments || []
        }));
      }
    } catch (error) {
      console.error('Error fetching comments:', error);
      setAllComments(prevComments => ({
        ...prevComments,
        [videoId]: []
      }));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (currentVideo && currentVideo.video_id) {
      fetchComments(currentVideo.video_id);
    }
  }, [currentVideo]);

  useEffect(() => {
    if (initialVideoIndex !== undefined) {
      setCurrentIndex(initialVideoIndex);
    }
  }, [initialVideoIndex]);

  const handlePrevious = (e) => {
    e.stopPropagation();
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? videos.length - 1 : prevIndex - 1
    );
  };

  const handleNext = (e) => {
    e.stopPropagation();
    setCurrentIndex((prevIndex) =>
      prevIndex === videos.length - 1 ? 0 : prevIndex + 1
    );
  };

  // Add event listener for keyboard navigation
  useEffect(() => {
    const keyHandler = (e) => {
      if (e.key === 'ArrowLeft') {
        e.stopPropagation();
        setCurrentIndex((prevIndex) =>
          prevIndex === 0 ? videos.length - 1 : prevIndex - 1
        );
      } else if (e.key === 'ArrowRight') {
        e.stopPropagation();
        setCurrentIndex((prevIndex) =>
          prevIndex === videos.length - 1 ? 0 : prevIndex + 1
        );
      } else if (e.key === 'Escape') {
        onClose();
      }
    };

    if (open) {
      window.addEventListener('keydown', keyHandler);
    }

    return () => {
      window.removeEventListener('keydown', keyHandler);
    };
  }, [open, videos.length, onClose]);

  const toggleComments = () => {
    setCommentsOpen(!commentsOpen);
  };

  const handleCommentChange = (e) => {
    setComment(e.target.value);
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (comment.trim() === '' || !currentVideo?.video_id) return;

    try {
      setIsLoading(true);
      const response = await axios.patch(
        `${API.CORE}/api/v1/video-analytics/update/comment/${currentVideo.video_id}`,
        { comment: comment }
      );

      if (response.data) {
        await fetchComments(currentVideo.video_id);
        setComment('');
      }
    } catch (error) {
      console.error('Error submitting comment:', error);
    } finally {
      setIsLoading(false);
    }
  };
  if (!currentVideo) return null;

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        TransitionComponent={Transition}
        PaperProps={{
          style: {
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            boxShadow: 'none',
            overflow: 'hidden',
            borderRadius: '8px',
            position: 'relative',
            maxWidth: commentsOpen ? '1400px' : '1100px',
            width: '100%',
            margin: '0 auto',
          },
        }}
      >
        <Box sx={{ position: 'relative', display: 'flex', height: '90vh' }}>
          <Box sx={{ position: 'relative', flexGrow: 1, padding: 2, display: 'flex', flexDirection: 'column' }}>
            <IconButton
              onClick={onClose}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: 'white',
                zIndex: 10,
              }}
            >
              <CloseIcon />
            </IconButton>

            {/* Comment button */}
            <CommentButton
              onClick={toggleComments}
              aria-label="toggle comments"
            >
              <CommentIcon />
            </CommentButton>

            <DialogContent sx={{ p: 0, overflow: 'hidden', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Box sx={{ position: 'relative', height: 'auto', width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <Box sx={{ position: 'relative', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <NavigationButton
                    onClick={handlePrevious}
                    sx={{ left: 16 }}
                    aria-label="previous video"
                  >
                    <PrevIcon />
                  </NavigationButton>

                  <VideoContainer>
                    <Box sx={{ position: 'relative', width: '100%', maxWidth: '1000px' }}>
                      <VideoFrame
                        src={getVideoEmbedUrl(currentVideo.url)}
                        title={`Video ${currentIndex + 1}`}
                        allowFullScreen
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        referrerPolicy="strict-origin-when-cross-origin"
                        style={{ border: 0 }}
                        loading="eager"
                        importance="high"
                        sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox allow-presentation"
                      />
                      <VideoOverlay>
                        <OverlayHeader>
                          <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold', textShadow: '1px 1px 3px rgba(0,0,0,0.8)' }}>
                            {currentVideo.name} - {currentVideo.program}
                          </Typography>
                        </OverlayHeader>
                        <OverlayFooter>
                          <Typography variant="body2" sx={{ color: 'white', textShadow: '1px 1px 3px rgba(0,0,0,0.8)' }}>
                            {currentVideo.name} - {currentVideo.program}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'white', textShadow: '1px 1px 3px rgba(0,0,0,0.8)' }}>
                            {currentIndex + 1} of {videos.length}
                          </Typography>
                        </OverlayFooter>
                      </VideoOverlay>
                    </Box>
                  </VideoContainer>

                  <NavigationButton
                    onClick={handleNext}
                    sx={{ right: 16 }}
                    aria-label="next video"
                  >
                    <NextIcon />
                  </NavigationButton>
                </Box>

              </Box>
            </DialogContent>
          </Box>

          {/* Comments panel inside dialog */}
          {commentsOpen && (
            <CommentPanel>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <FeedbackIcon sx={{ mr: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 500 }}>Video Feedback</Typography>
                </Box>
                <IconButton
                  onClick={() => setCommentsOpen(false)}
                  sx={{
                    color: 'white',
                    padding: '4px',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)'
                    }
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>

              <Divider sx={{ backgroundColor: '#333', mb: 2 }} />

              <CommentList>
                {isLoading ? (
                  <Typography variant="body2" sx={{ color: '#999', textAlign: 'center', py: 2 }}>
                    Loading comments...
                  </Typography>
                ) : allComments[currentVideo?.video_id]?.length > 0 ? (
                  allComments[currentVideo.video_id].map((commentItem, index) => (
                    <React.Fragment key={index}>
                      <CommentItem>
                        <CommentText>{commentItem.comment || commentItem.text}</CommentText>
                        <CommentDate>
                          {formatTimeAgo(commentItem.timestamp || commentItem.date)} ago
                        </CommentDate>
                      </CommentItem>
                    </React.Fragment>
                  ))
                ) : (
                  <Typography variant="body2" sx={{ color: '#999', textAlign: 'center', py: 2 }}>
                    No comments yet. Be the first to comment!
                  </Typography>
                )}
              </CommentList>

              <CommentForm onSubmit={handleSubmitComment}>
                <CommentInput
                  placeholder="Add a comment..."
                  value={comment}
                  onChange={handleCommentChange}
                  variant="outlined"
                  size="small"
                  fullWidth
                  disabled={isLoading}
                />
                <CommentSubmitButton 
                  type="submit" 
                  aria-label="send comment"
                  disabled={isLoading}
                >
                  <SendIcon />
                </CommentSubmitButton>
              </CommentForm>
            </CommentPanel>
          )}
        </Box>
      </Dialog>
    </>
  );
};

export default VideoPreviewCarousel;
